/**
 * Helsec embedded-viewer authentication.
 *
 * The viewer runs inside an iframe on the Django app (task_detail.html). The
 * iframe URL deliberately carries a DECOY token (`access_token=`, a canary that
 * the DICOM proxy rejects and audits); the REAL bearer token is handed over by
 * the parent via `postMessage({ type: 'SET_AUTH_TOKEN', token })`.
 *
 * Two properties matter for this to work reliably:
 *
 *  1. The message listener must be registered at module load, unconditionally.
 *     Previously it was created inside updateAuthServiceAndCleanUrl(), which
 *     Mode.tsx only called when a `token=` URL param existed. The Helsec iframe
 *     uses `access_token=`, so the listener never existed and every
 *     SET_AUTH_TOKEN from the parent was dropped -> no Authorization header ->
 *     403 on the first QIDO -> /notfoundstudy.
 *
 *  2. The first DICOMweb query must wait for the token. Mode.tsx validates the
 *     study with a QIDO as soon as the route mounts, before the parent's
 *     postMessage (sent on iframe `load` + retries) has arrived, and it never
 *     retries on failure. waitForEmbeddedAuthToken() lets the route gate its
 *     first request on token arrival (with a timeout so standalone use is
 *     unaffected).
 *
 * The trusted parent origin is scheme + host only (never a path). It defaults
 * to the test environment and can be overridden at runtime from app-config.js
 * (`window.config.embedAuthOrigin`) so one build artifact can serve several
 * environments without a rebuild.
 */

const DEFAULT_TRUSTED_EMBED_ORIGIN = 'https://test-helsec.no';
const EMBED_TOKEN_WAIT_MS = 8000;

type AuthServiceLike = {
  setServiceImplementation: (impl: {
    getAuthorizationHeader: () => { Authorization: string };
  }) => void;
};

function trustedEmbedOrigin(): string {
  const cfg = (window as any).config;
  return (cfg && typeof cfg.embedAuthOrigin === 'string' && cfg.embedAuthOrigin) ||
    DEFAULT_TRUSTED_EMBED_ORIGIN;
}

/** True when the viewer is embedded in a frame (the Helsec case view). */
function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin parent throws on access -> we are embedded.
    return true;
  }
}

let embeddedToken: string | null = null;
let authService: AuthServiceLike | null = null;
let resolveTokenReady: (() => void) | null = null;
const tokenReady: Promise<void> = new Promise(resolve => {
  resolveTokenReady = resolve;
});

function applyToken(): void {
  if (!embeddedToken || !authService) {
    return;
  }
  const token = embeddedToken;
  authService.setServiceImplementation({
    getAuthorizationHeader: () => ({ Authorization: 'Bearer ' + token }),
  });
}

// Registered once, at module load, so it is live before the parent's first
// postMessage. Also handles later token refreshes from the parent.
window.addEventListener('message', event => {
  if (event.origin !== trustedEmbedOrigin()) {
    return;
  }
  const data = event.data;
  if (data && data.type === 'SET_AUTH_TOKEN' && typeof data.token === 'string' && data.token) {
    embeddedToken = data.token;
    applyToken();
    if (resolveTokenReady) {
      resolveTokenReady();
      resolveTokenReady = null;
    }
  }
});

/**
 * Resolves once the parent has delivered a token, or after a timeout. Resolves
 * immediately when not embedded (standalone / dev use) or when a token has
 * already been received.
 */
export function waitForEmbeddedAuthToken(timeoutMs: number = EMBED_TOKEN_WAIT_MS): Promise<boolean> {
  if (embeddedToken) {
    return Promise.resolve(true);
  }
  if (!isEmbedded()) {
    return Promise.resolve(false);
  }
  return new Promise(resolve => {
    const timer = window.setTimeout(() => {
      console.warn('OHIF: no SET_AUTH_TOKEN from parent within ' + timeoutMs + 'ms; continuing without it.');
      resolve(false);
    }, timeoutMs);
    tokenReady.then(() => {
      window.clearTimeout(timer);
      resolve(true);
    });
  });
}

/**
 * Binds the auth service (applies a token that may already have arrived) and
 * strips any `token` query param from browser history. The URL token itself is
 * never used for authentication - only the postMessage token is.
 *
 * @param token - `token` URL query param, if any (only used to know it must be cleaned).
 * @param location - The location object from the router.
 * @param userAuthenticationService - The user authentication service instance.
 */
export function updateAuthServiceAndCleanUrl(
  token: string | null | undefined,
  location: any,
  userAuthenticationService: AuthServiceLike
): void {
  authService = userAuthenticationService;
  applyToken();

  if (token) {
    const urlObj = new URL(window.location.origin + window.location.pathname + location.search);
    urlObj.searchParams.delete('token');
    const cleanUrl = urlObj.toString();

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', cleanUrl);
    }
  }
}
